import { MutationCache, QueryClient } from '@tanstack/react-query';

import { getToastState, toast } from '@/hooks/use-toast';
import {
  getErrorMessage,
  getPermissionDeniedMessage,
  normalizeApiErrorMessage,
} from '@/lib/error-utils';

interface ErrorResponseLike {
  status?: unknown;
}

interface ErrorWithResponseLike {
  response?: ErrorResponseLike;
  code?: unknown;
}

function getErrorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const candidate = error as ErrorWithResponseLike;
  const response = candidate.response;
  if (typeof response?.status === 'number') return response.status;
  const code = candidate.code;
  return typeof code === 'number' ? code : undefined;
}

interface MutationErrorToastLike {
  options?: {
    onError?: unknown;
    meta?: unknown;
  };
}

function hasLocalErrorToastHandler(mutation: MutationErrorToastLike): boolean {
  if (typeof mutation.options?.onError === 'function') return true;

  const meta = mutation.options?.meta;
  if (!meta || typeof meta !== 'object') return false;
  return (meta as Record<string, unknown>).suppressGlobalErrorToast === true;
}

function getActiveToastIds(): Set<string> {
  return new Set(
    getToastState()
      .toasts.filter((item) => item.open !== false)
      .map((item) => item.id)
  );
}

function hasNewActiveToast(previousIds: Set<string>): boolean {
  return getToastState().toasts.some((item) => item.open !== false && !previousIds.has(item.id));
}

export function showGlobalMutationErrorToastIfUnhandled(
  error: unknown,
  mutation: MutationErrorToastLike
) {
  if (hasLocalErrorToastHandler(mutation)) return;

  const activeToastIdsBeforeLocalHandlers = getActiveToastIds();
  const status = getErrorStatus(error);
  if (status === 401) return;

  globalThis.setTimeout(() => {
    if (hasNewActiveToast(activeToastIdsBeforeLocalHandlers)) return;

    if (status === 403) {
      toast({
        title: '暂无权限',
        description: getPermissionDeniedMessage(error),
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: '请求失败',
      description: normalizeApiErrorMessage(getErrorMessage(error, '请求失败，请稍后重试')),
      variant: 'destructive',
    });
  }, 0);
}

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      showGlobalMutationErrorToastIfUnhandled(error, mutation);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 分钟
      gcTime: 1000 * 60 * 30, // 30 分钟
      retry: (failureCount, error) => {
        // 对于客户端错误(4xx)不重试,避免无意义的重复请求
        const status = getErrorStatus(error);
        if (status !== undefined && status >= 400 && status < 500) {
          return false;
        }

        // 其他错误(网络错误、5xx等)最多重试 2 次
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: (failureCount, error) => {
        const status = getErrorStatus(error);
        if (status !== undefined && status >= 400 && status < 500) {
          return false;
        }
        return failureCount < 1;
      },
    },
  },
});
